import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../components/layout/AppLayout";
import { productService } from "../../services/productService";
import { salesService } from "../../services/salesService";
import { useAuth } from "../../context/AuthContext";
import { useNotification } from "../../context/NotificationContext";

const SalesNote = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  // --- MEMORI (STATE): Pencarian & Pemilihan Barang ---
  const [searchQuery, setSearchQuery] = useState(""); // teks pencarian
  const [searchResults, setSearchResults] = useState([]); // hasil pencarian dari database
  const [selectedProduct, setSelectedProduct] = useState(null); // barang yang sedang diklik
  const [quantity, setQuantity] = useState(1); // jumlah yang mau dibeli

  // --- MEMORI (STATE): Keranjang & Data Transaksi ---
  const [customerName, setCustomerName] = useState(""); // nama pembeli
  const [cart, setCart] = useState([]); // daftar barang yang sudah masuk nota
  const [transactionId, setTransactionId] = useState(""); // ID unik transaksi (SN-XXXX)
  const [isSubmitting, setIsSubmitting] = useState(false); // status saat menekan tombol selesaikan
  const [isSearching, setIsSearching] = useState(false); // status saat mencari barang

  /**
   * --- LOGIKA OTOMATIS: Membuat ID Transaksi ---
   * Membuat kode unik berdasarkan tanggal (Contoh: SN-20231025-1234).
   */
  useEffect(() => {
    const date = new Date();
    const stamp = date.getTime().toString().slice(-4);
    const id = `SN-${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, "0")}${date.getDate().toString().padStart(2, "0")}-${stamp}`;
    setTransactionId(id);
  }, []);

  /**
   * --- LOGIKA: Mencari Produk ---
   * Mencari barang berdasarkan Nama atau SKU di Database.
   */
  const performSearch = useCallback(async () => {
    try {
      setIsSearching(true);
      const data = await productService.searchProducts(searchQuery, 5);
      setSearchResults(data || []);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  // Memberi jeda (debounce) saat mengetik agar tidak terlalu sering memanggil database
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        performSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  /**
   * --- LOGIKA: Masukkan ke Keranjang ---
   * Memvalidasi stok sebelum barang bisa masuk ke dalam nota.
   */
  const addToCart = () => {
    if (!selectedProduct) return;

    // 1. Validasi Stok: Tidak boleh beli lebih dari stok yang ada
    if (quantity > selectedProduct.stock_level) {
      showNotification({
        type: "error",
        title: "Stok Tidak Cukup",
        message: `Stok saat ini hanya ${selectedProduct.stock_level} unit.`,
      });
      return;
    }

    // 2. Tambah atau Perbarui Keranjang
    const existingIndex = cart.findIndex(
      (item) => item.product_id === selectedProduct.id,
    );
    const newCart = [...cart];

    if (existingIndex >= 0) {
      // Jika barang sudah ada di keranjang, tambah jumlahnya saja
      const newQty = newCart[existingIndex].quantity + quantity;
      if (newQty > selectedProduct.stock_level) {
        showNotification({
          type: "error",
          title: "Stok Tidak Cukup",
          message: "Total kuantitas melebihi stok yang tersedia.",
        });
        return;
      }
      newCart[existingIndex].quantity = newQty;
      newCart[existingIndex].subtotal =
        newCart[existingIndex].quantity * selectedProduct.price;
    } else {
      // Jika barang belum ada, buat baris baru di keranjang
      newCart.push({
        product_id: selectedProduct.id,
        name: selectedProduct.name,
        sku: selectedProduct.sku,
        quantity: quantity,
        unit_price: selectedProduct.price,
        subtotal: quantity * selectedProduct.price,
      });
    }

    setCart(newCart);
    setSelectedProduct(null);
    setSearchQuery("");
    setQuantity(1);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // --- LOGIKA HITUNG-HITUNGAN TOTAL ---
  const subtotal = cart.reduce((acc, item) => acc + Number(item.subtotal), 0);
  const tax = subtotal * 0; // PPN 0%
  const total = subtotal + tax;

  /**
   * --- LOGIKA UTAMA: Selesaikan Transaksi ---
   * 1. Menyimpan data penjualan (Siapa pembelinya, berapa totalnya).
   * 2. Menyimpan rincian barang yang dibeli.
   * 3. Mengurangi stok barang secara otomatis di database.
   */
  const handleCreateSale = async () => {
    if (cart.length === 0) {
      showNotification({
        type: "warning",
        title: "Keranjang Kosong",
        message: "Tambahkan minimal satu produk ke daftar penjualan.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Step 1: Siapkan Data Ringkasan Penjualan
      const salePayload = {
        transaction_id: transactionId,
        customer_name: customerName || "Pelanggan Umum",
        sales_person_id: user?.id,
        subtotal: subtotal,
        tax: tax,
        total: total,
        status: "Completed",
      };

      // Step 2: Siapkan Data Rincian Barang
      const saleItemsPayload = cart.map((item) => ({
        product_id: item.product_id,
        product_name: item.name,
        product_sku: item.sku,
        quantity: item.quantity,
        unit_price: item.unit_price,
        subtotal: item.subtotal,
      }));

      // Kirim semuanya ke database melalui 'Layanan Penjualan'
      await salesService.createSaleRecord(salePayload, saleItemsPayload);

      showNotification({
        type: "success",
        title: "Penjualan Berhasil",
        message: "Transaksi selesai! Stok inventaris telah diperbarui.",
      });
      navigate("/dashboard");
    } catch (error) {
      console.error("Checkout error:", error);
      showNotification({
        type: "error",
        title: "Checkout Gagal",
        message: "Gagal menyelesaikan transaksi: " + error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
    }).format(val);
  };

  return (
    <AppLayout pageTitle="Catatan Penjualan">
      <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6 md:gap-8 min-h-full">
        {/* SISI KIRI: Input & Pemilihan Barang */}
        <div className="col-span-12 lg:col-span-7 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-2">
            <div className="space-y-1">
              <h2 className="font-headline text-2xl md:text-3xl font-extrabold tracking-tight text-on-surface leading-tight">
                Catatan Penjualan Digital
              </h2>
              <p className="text-on-surface-variant text-xs md:text-sm max-w-md">
                Catat transaksi dan sinkronisasi inventaris secara real-time.
              </p>
            </div>
            <div className="flex flex-col items-start sm:items-end bg-primary/5 px-4 py-3 rounded-2xl border border-primary/10 w-full sm:w-auto">
              <p className="text-[9px] font-label uppercase tracking-[0.2em] text-primary font-black">
                ID Transaksi
              </p>
              <p className="font-mono text-sm font-black text-primary mt-0.5">
                {transactionId}
              </p>
            </div>
          </div>

          {/* Kartu: Data Pembeli */}
          <div className="bg-surface-container-lowest rounded-2xl p-5 md:p-6 shadow-sm ring-1 ring-outline-variant/30">
            <div className="flex items-center gap-2 mb-4">
              <span className="material-symbols-outlined text-primary text-lg">
                person
              </span>
              <h3 className="font-headline text-sm md:text-base font-bold uppercase tracking-tight">
                Data Pembeli
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-label uppercase tracking-wider text-on-surface-variant mb-1.5 ml-1 font-bold">
                  Nama Pelanggan
                </label>
                <input
                  className="w-full bg-surface-container-highest border-none rounded-xl py-2.5 px-4 text-sm focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all shadow-inner"
                  placeholder="e.g. Marcus Wright"
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] font-label uppercase tracking-wider text-on-surface-variant mb-1.5 ml-1 font-bold">
                  Nama Penjual
                </label>
                <input
                  className="w-full bg-surface-container border-none rounded-xl py-2.5 px-4 text-sm text-on-surface-variant opacity-60 italic font-medium"
                  disabled
                  type="text"
                  value={
                    user?.user_metadata?.full_name ||
                    user?.email ||
                    "Current User"
                  }
                />
              </div>
            </div>
          </div>

          {/* Kartu: Cari Produk */}
          <div className="bg-surface-container-lowest rounded-2xl p-5 md:p-6 shadow-sm ring-1 ring-outline-variant/30 relative overflow-visible">
            <div className="flex items-center gap-2 mb-4">
              <span className="material-symbols-outlined text-primary text-lg">
                inventory_2
              </span>
              <h3 className="font-headline text-sm md:text-base font-bold uppercase tracking-tight">
                Alokasi Stok
              </h3>
            </div>
            <div className="space-y-4">
              <div className="relative">
                <label className="block text-[10px] font-label uppercase tracking-wider text-on-surface-variant mb-1.5 ml-1 font-bold">
                  Cari Produk (SKU/Nama)
                </label>
                <div className="relative">
                  <span
                    className={`material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg ${isSearching ? "animate-spin" : ""}`}
                  >
                    {isSearching ? "sync" : "search"}
                  </span>
                  <input
                    className="w-full pl-11 pr-4 py-3 bg-surface-container-highest border-none rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-primary-fixed-dim transition-all shadow-inner"
                    placeholder="Cari Produk"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* TAMPILAN: Hasil Pencarian (Dropdown) */}
                {searchResults.length > 0 &&
                  searchQuery &&
                  !selectedProduct && (
                    <div className="absolute top-full left-0 right-0 mt-3 bg-surface-container-high rounded-2xl shadow-2xl z-[60] overflow-hidden ring-1 ring-outline-variant animate-in fade-in slide-in-from-top-2 duration-200">
                      {searchResults.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => {
                            setSelectedProduct(p);
                            setSearchQuery(p.name);
                          }}
                          className="w-full flex items-center gap-4 p-4 hover:bg-surface-bright text-left border-b border-outline-variant/10 last:border-0 transition-colors"
                        >
                          <div className="w-12 h-12 rounded-lg bg-primary-container/20 flex items-center justify-center shrink-0 overflow-hidden border border-outline-variant/20">
                            {p.image_url ? (
                              <img
                                src={p.image_url}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="material-symbols-outlined text-sm text-primary">
                                image
                              </span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-black text-on-surface leading-tight">
                              {p.name}
                            </p>
                            <p className="text-[10px] text-outline font-bold uppercase tracking-wider mt-0.5">
                              {p.sku} • {p.stock_level} UNIT
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-black text-primary">
                              {formatCurrency(p.price)}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
              </div>

              {/* Input Jumlah Barang */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 pt-6 border-t border-outline-variant/10">
                <div className="col-span-1">
                  <label className="block text-[10px] font-label uppercase tracking-wider text-on-surface-variant mb-1.5 ml-1 font-bold">
                    Jumlah
                  </label>
                  <div className="flex items-center p-1 bg-surface-container-highest rounded-xl shadow-inner">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 flex items-center justify-center hover:bg-white rounded-lg transition-all text-on-surface"
                    >
                      <span className="material-symbols-outlined text-sm">
                        remove
                      </span>
                    </button>
                    <input
                      className="w-full text-center bg-transparent border-none text-sm font-black focus:ring-0 text-primary"
                      type="number"
                      value={quantity}
                      onChange={(e) =>
                        setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                      }
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 flex items-center justify-center hover:bg-white rounded-lg transition-all text-on-surface"
                    >
                      <span className="material-symbols-outlined text-sm">
                        add
                      </span>
                    </button>
                  </div>
                </div>
                <div className="sm:col-span-2 flex items-end">
                  {/* Tombol Masukkan Keranjang */}
                  <button
                    disabled={!selectedProduct}
                    onClick={addToCart}
                    className="w-full py-3.5 bg-primary text-on-primary rounded-xl font-black text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 hover:bg-primary-container hover:text-on-primary-container transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-xl shadow-primary/20"
                  >
                    <span className="material-symbols-outlined text-lg">
                      add_shopping_cart
                    </span>
                    Tambah ke Nota
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SISI KANAN: Nota & Ringkasan Pembayaran */}
        <div className="col-span-12 lg:col-span-5 flex flex-col h-full">
          <div className="bg-surface-container-lowest rounded-3xl shadow-2xl flex flex-col h-auto lg:h-[calc(100vh-140px)] ring-1 ring-outline-variant/20 overflow-hidden relative border border-outline-variant/30">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-12 translate-x-12 blur-3xl opacity-50"></div>

            {/* Header Nota */}
            <div className="p-6 md:p-8 text-center border-b border-dashed border-outline-variant bg-surface-container-low/30 relative">
              <h4 className="font-headline text-lg font-black tracking-tighter uppercase">
                {user?.user_metadata?.store_name || "Harapan Kita"}
              </h4>
            </div>

            {/* Daftar Barang yang Ditambahkan */}
            <div className="flex-1 p-6 md:p-8 overflow-y-auto custom-scrollbar min-h-[250px]">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center opacity-20 gap-3">
                  <span className="material-symbols-outlined text-6xl">
                    pending_actions
                  </span>
                  <p className="text-[10px] font-black uppercase tracking-[0.3em]">
                    Nota Kosong
                  </p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[9px] font-label uppercase tracking-widest text-on-surface-variant text-left opacity-60">
                      <th className="pb-4 font-black">Spesifikasi Barang</th>
                      <th className="pb-4 font-black text-center">Jumlah</th>
                      <th className="pb-4 font-black text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/10">
                    {cart.map((item, idx) => (
                      <tr key={idx} className="group relative">
                        <td className="py-4 pr-2">
                          <p className="font-black text-on-surface leading-tight">
                            {item.name}
                          </p>
                          <p className="text-[9px] text-on-surface-variant font-mono uppercase mt-0.5">
                            {item.sku}
                          </p>
                        </td>
                        <td className="py-4 text-center font-bold text-on-surface-variant">
                          {item.quantity}
                        </td>
                        <td className="py-4 text-right">
                          <p className="font-black text-on-surface">
                            {formatCurrency(item.subtotal)}
                          </p>
                          <button
                            onClick={() => removeFromCart(idx)}
                            className="text-[9px] text-error font-black uppercase hover:underline opacity-0 lg:opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            Hapus
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Ringkasan Total & Tombol Checkout */}
            <div className="p-6 md:p-8 bg-surface-container-low/40 backdrop-blur-sm mt-auto border-t border-outline-variant/30">
              <div className="space-y-3 mb-6 md:mb-8">
                <div className="flex justify-between items-center text-[10px] text-on-surface-variant font-bold uppercase tracking-wider">
                  <span>Subtotal</span>
                  <span className="text-on-surface">
                    {formatCurrency(subtotal)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-[10px] text-on-surface-variant font-bold uppercase tracking-wider">
                  <span>PPN (0%)</span>
                  <span className="text-on-surface">{formatCurrency(tax)}</span>
                </div>
                <div className="pt-5 mt-2 border-t border-dashed border-outline-variant/50">
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-label uppercase tracking-[0.3em] text-primary font-black">
                      Total Tagihan
                    </span>
                    <span className="font-headline text-4xl md:text-5xl font-black text-on-surface tracking-tighter leading-none py-2">
                      {formatCurrency(total)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {/* Tombol Utama */}
                <button
                  disabled={isSubmitting || cart.length === 0}
                  onClick={handleCreateSale}
                  className="w-full py-4 bg-primary text-on-primary rounded-2xl font-black text-xs md:text-sm uppercase tracking-widest shadow-2xl shadow-primary/30 flex items-center justify-center gap-3 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-30 disabled:grayscale"
                >
                  <span
                    className={`material-symbols-outlined ${isSubmitting ? "animate-spin" : ""}`}
                  >
                    {isSubmitting ? "sync" : "verified"}
                  </span>
                  {isSubmitting ? "Memproses..." : "Selesaikan Transaksi"}
                </button>
                <button
                  onClick={() => setCart([])}
                  className="w-full py-3 text-on-surface-variant border border-outline-variant/50 rounded-xl font-bold text-[10px] hover:bg-white transition-all uppercase tracking-[0.2em] opacity-60 hover:opacity-100"
                >
                  Hapus Nota
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default SalesNote;
